import { useEffect, useRef, useState } from "react";
import { storage } from "../../state/storage.js";
import { importAssetFromFile, releaseAsset } from "../../platform/assets/index.js";
import { AssetImage } from "./AssetImage.js";
import { useT } from "../../i18n/use-t.js";
import { createCharacter, type Character, type Lorebook, type Script } from "@roqeet/core";

/**
 * 캐릭터 에디터(생성/편집). 카드 임포트(결정 #6)와 별개로 직접 캐릭터를 만들고
 * 다듬는 경로. 텍스트 필드는 Roqeet 인라인 값을 그대로 담는다. 저장 시 전개하지
 * 않고 원문 보존(렌더/프롬프트 시점에 전개)한다.
 *
 * `character`가 없으면 새 캐릭터, 있으면 편집. 저장은 storage 포트를 통해
 * sync 메타가 갱신된다(#20). 로어북(결정 #12) + 정규식 스크립트(결정 #21)를 연결.
 */
export function CharacterEditor({
  character,
  onClose,
}: {
  character?: Character;
  onClose: () => void;
}) {
  const isNew = !character;
  const { t } = useT();
  const [name, setName] = useState(character?.name ?? "");
  const [description, setDescription] = useState(character?.description ?? "");
  const [personality, setPersonality] = useState(character?.personality ?? "");
  const [scenario, setScenario] = useState(character?.scenario ?? "");
  const [firstMessage, setFirstMessage] = useState(character?.firstMessage ?? "");
  const [altGreetings, setAltGreetings] = useState((character?.alternateGreetings ?? []).join("\n---\n"));
  const [messageExample, setMessageExample] = useState(character?.messageExample ?? "");
  const [systemPrompt, setSystemPrompt] = useState(character?.systemPrompt ?? "");
  const [tags, setTags] = useState((character?.tags ?? []).join(", "));
  const [avatarAssetId, setAvatarAssetId] = useState(character?.avatarAssetId);
  const [lorebookId, setLorebookId] = useState(character?.lorebookId);
  const [lorebooks, setLorebooks] = useState<Lorebook[]>([]);
  const [scripts, setScripts] = useState<Script[]>([]);
  const [scriptIds, setScriptIds] = useState<string[]>(character?.scriptIds ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void storage.lorebooks.list().then(setLorebooks);
    // 정규식 + JS 스크립트 모두 연결 가능(결정 #4/#21).
    void storage.scripts
      .list()
      .then((all) => setScripts(all.filter((s) => s.language === "regex" || s.language === "javascript")));
  }, []);

  const onAvatarChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const asset = await importAssetFromFile(file);
      if (avatarAssetId) await releaseAsset(avatarAssetId).catch(() => {});
      setAvatarAssetId(asset.id);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      setErr(t("charEd.errNameRequired"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const greetings = altGreetings
        .split("\n---\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const tagList = tags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      if (isNew) {
        const c = createCharacter({
          name: name.trim(),
          description,
          firstMessage,
          alternateGreetings: greetings,
          tags: tagList,
          scriptIds: scriptIds as Character["scriptIds"],
          ...(personality ? { personality } : {}),
          ...(scenario ? { scenario } : {}),
          ...(messageExample ? { messageExample } : {}),
          ...(systemPrompt ? { systemPrompt } : {}),
          ...(avatarAssetId ? { avatarAssetId } : {}),
          ...(lorebookId ? { lorebookId } : {}),
        });
        await storage.characters.put(c);
      } else {
        const next: Character = {
          ...character,
          name: name.trim(),
          description,
          personality,
          scenario,
          firstMessage,
          alternateGreetings: greetings,
          messageExample,
          systemPrompt,
          tags: tagList,
          scriptIds: scriptIds as Character["scriptIds"],
          ...(avatarAssetId ? { avatarAssetId } : {}),
        };
        // 로어북은 해제(빈 선택)도 허용하므로 조건부가 아니라 직접 대입.
        if (lorebookId) next.lorebookId = lorebookId;
        else delete next.lorebookId;
        await storage.characters.put(next);
      }
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-bg" role="dialog" aria-modal="true">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarChosen} />

      {/* 헤더 */}
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-3">
        <span className="text-sm font-semibold text-ink">
          {isNew ? t("char.newCharacter") : t("charEd.editTitle")}
        </span>
        <div className="flex gap-2">
          <button
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={onClose}
            disabled={busy}
          >
            {t("common.cancel")}
          </button>
          <button
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
            onClick={save}
            disabled={busy}
          >
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>

      {/* 본문 */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4 scroll-touch">
        <div className="mx-auto max-w-2xl space-y-4">
          {/* 아바타 + 이름 */}
          <div className="flex items-center gap-3">
            <button
              className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-line"
              onClick={() => fileRef.current?.click()}
              title={t("persona.avatarChange")}
            >
              <AssetImage
                assetId={avatarAssetId}
                alt={name || t("char.avatar")}
                className="h-full w-full object-cover"
                fallback={<span className="text-2xl">{name.slice(0, 1) || "?"}</span>}
              />
            </button>
            <Field label={t("charEd.fieldName")} className="flex-1">
              <input
                className="w-full rounded-lg border border-line bg-bg-soft px-3 py-2 text-ink outline-none focus:border-accent"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("charEd.namePlaceholder")}
              />
            </Field>
          </div>

          <Field label={t("charEd.fieldDescription")} hint={t("charEd.hintRoqeetInline")}>
            <Area value={description} onChange={setDescription} rows={5} />
          </Field>
          <Field label={t("charEd.fieldPersonality")}>
            <Area value={personality} onChange={setPersonality} rows={2} />
          </Field>
          <Field label={t("charEd.fieldScenario")}>
            <Area value={scenario} onChange={setScenario} rows={2} />
          </Field>
          <Field label={t("charEd.fieldFirstMessage")}>
            <Area value={firstMessage} onChange={setFirstMessage} rows={3} />
          </Field>
          <Field label={t("charEd.fieldAltGreetings")} hint={t("charEd.hintAltGreetings")}>
            <Area value={altGreetings} onChange={setAltGreetings} rows={3} />
          </Field>
          <Field label={t("charEd.fieldMessageExample")}>
            <Area value={messageExample} onChange={setMessageExample} rows={3} />
          </Field>
          <Field label={t("charEd.fieldSystemPrompt")} hint={t("charEd.hintSystemPrompt")}>
            <Area value={systemPrompt} onChange={setSystemPrompt} rows={3} />
          </Field>
          <Field label={t("charEd.fieldTags")} hint={t("charEd.hintTags")}>
            <input
              className="w-full rounded-lg border border-line bg-bg-soft px-3 py-2 text-ink outline-none focus:border-accent"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t("charEd.tagsPlaceholder")}
            />
          </Field>
          <Field label={t("charEd.fieldLorebook")} hint={t("charEd.hintLorebook")}>
            <select
              className="w-full rounded-lg border border-line bg-bg-soft px-3 py-2 text-ink outline-none focus:border-accent"
              value={lorebookId ?? ""}
              onChange={(e) =>
                setLorebookId(e.target.value ? (e.target.value as Character["lorebookId"]) : undefined)
              }
            >
              <option value="">{t("charEd.lorebookNone")}</option>
              {lorebooks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.entries.length})
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("charEd.fieldScripts")} hint={t("charEd.hintScripts")}>
            {scripts.length === 0 ? (
              <p className="text-xs text-ink-faint">
                {t("charEd.noScripts")}
              </p>
            ) : (
              <ul className="space-y-1 rounded-lg border border-line bg-bg-soft p-2">
                {scripts.map((s) => (
                  <li key={s.id}>
                    <label className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={scriptIds.includes(s.id)}
                        onChange={() =>
                          setScriptIds((cur) =>
                            cur.includes(s.id) ? cur.filter((x) => x !== s.id) : [...cur, s.id],
                          )
                        }
                      />
                      {s.name}
                      <span className="rounded bg-bg-raised px-1.5 py-0.5 text-xs text-ink-faint">
                        {s.language === "regex" ? t("charEd.langRegex") : "JS"}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </Field>

          {err && <p className="text-sm text-red-300">{err}</p>}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={["block", className].filter(Boolean).join(" ")}>
      <span className="mb-1 block text-xs text-ink-soft">
        {label}
        {hint && <span className="ml-1 text-ink-faint">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function Area({
  value,
  onChange,
  rows,
}: {
  value: string;
  onChange: (v: string) => void;
  rows: number;
}) {
  return (
    <textarea
      className="w-full resize-y rounded-lg border border-line bg-bg-soft px-3 py-2 text-ink outline-none focus:border-accent"
      rows={rows}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
